/**
 * Logica del <ActivityStepper/>: de los 7 estados reales de la actividad a los 5 hitos de la linea de tiempo
 * (Borrador -> En revision -> Observado/Corregido -> Aprobado -> Cerrado). El flujo real NO es lineal (una actividad
 * aprobada puede volver a observarse, una cerrada a revision): el hito es una fotografia del estado actual, no un recorrido.
 */
import { ACTIVITY_STATUS_LABEL, type ActivityStatus } from '@rat/shared';

export type StepState = 'done' | 'current' | 'upcoming' | 'skipped';
export type StepTone = 'info' | 'accent' | 'warning' | 'success' | 'neutral';
export interface StepNode {
  label: string;
  state: StepState;
  tone: StepTone;
}

/** Hito (0..4) al que pertenece cada estado. EN_COMPLETADO es un borrador con avance; OBSERVADO y CORREGIDO comparten hito. */
const STAGE: Record<ActivityStatus, number> = {
  BORRADOR: 0,
  EN_COMPLETADO: 0,
  EN_REVISION: 1,
  OBSERVADO: 2,
  CORREGIDO: 2,
  APROBADO: 3,
  CERRADO: 4,
};
const OBSERVED_STAGE = 2;
// Mismos tonos que ACT_TONE de components/ui.tsx: el hito actual se ve igual que la pastilla de estado.
const TONE: Record<ActivityStatus, StepTone> = {
  BORRADOR: 'info',
  EN_COMPLETADO: 'info',
  EN_REVISION: 'accent',
  OBSERVADO: 'warning',
  CORREGIDO: 'warning',
  APROBADO: 'success',
  CERRADO: 'neutral',
};
export const STAGE_LABELS = ['Borrador', 'En revisión', 'Observado / Corregido', 'Aprobado', 'Cerrado'] as const;
/** Tono de cada hito cuando es el actual (el de sus estados "tipicos"); lo usan tambien los graficos por hito. */
export const STAGE_TONES: readonly StepTone[] = ['info', 'accent', 'warning', 'success', 'neutral'];

/** Hito (0..4) de un estado, o undefined si el backend trajo uno que este mapa no conoce (no se cuenta en silencio en otro hito). */
export function stageOf(status: string): number | undefined {
  return STAGE[status as ActivityStatus];
}

/**
 * `hadObservations`: si la actividad recibio alguna observacion en su vida. Si es `false`, el hito "Observado / Corregido"
 * se marca como omitido en vez de completado (aprobar de primera es un camino valido y un check ahi diria algo que no
 * ocurrio). Si se desconoce (`undefined`, p. ej. aun cargando) se asume completado.
 */
export function stepperNodes(status: ActivityStatus, hadObservations?: boolean): StepNode[] {
  const current = STAGE[status];
  return STAGE_LABELS.map((label, i) => {
    if (i === current) return { label: i === OBSERVED_STAGE ? ACTIVITY_STATUS_LABEL[status] : label, state: 'current', tone: TONE[status] };
    if (i > current) return { label, state: 'upcoming', tone: 'neutral' };
    return { label, state: i === OBSERVED_STAGE && hadObservations === false ? 'skipped' : 'done', tone: 'neutral' };
  });
}
